import React, { useContext, useMemo, useState } from "react";
import Title from "../../../components/Title";
import AppContext, { IFilters } from "../../../AppContext";
import { gql, useQuery } from "@apollo/client";
import MapGraph from "../../../components/MapGraph/MapChartSwitcher";
import { MessageBar } from "@fluentui/react";
import "./style.scss";
import { useTranslation } from "react-i18next";
import { Entorno } from "../../../utils/constants";

export const titleEs = "Regional position";
export const title = "Tendencia por región";
export const filters: IFilters = {
  limitedPeriodo: false,
  showMedidas: true,
  hasTicketMedio: true,
  isDesglose: false,
  showEntorno: true,
  showSegmentos: true,
  showMyData: false,
};

function translateExportKeys(
  input: any,
  t: (k: string) => string,
  isPT = false,
  isIRL = false
): any {
  const translateKey = (raw: string): string => {
    if (!raw || typeof raw !== "string") return raw as any;
    let s = raw.replace(/_/g, " ").trim();
    const low = s.toLowerCase();

    // Irlanda
    if (isIRL) {
      const zoneMapIRL: Record<string, string> = {
        madrid:    "Dublin",
        barcelona: t("Northern&Western") || "Northern & Western",
        "zona 3":  t("Eastern&Midland")  || "Eastern & Midland",
        "zona 4":  t("Southern")         || "Southern",
        "zona 5":  "",
        "zona 6":  "",
      };
      if (zoneMapIRL[low] !== undefined) return zoneMapIRL[low];
    }

    // Portugal
    if (isPT) {
      if (low === "madrid") return "Lisboa";
      if (low === "barcelona") return "Porto";
    }

    const zoneMapPT: Record<string, string> = {
      "zona 3": t("North")     || "North",
      "zona 4": t("Center")    || "Center",
      "zona 5": "Alentejo",
      "zona 6": "Algarve",
    };
    const zoneMapES: Record<string, string> = {
      "zona 3": t("North")     || "North",
      "zona 4": t("Center")    || "Center",
      "zona 5": t("Northeast") || "Northeast",
      "zona 6": t("South")     || "South",
    };
    if (isPT && zoneMapPT[low]) return zoneMapPT[low];
    if (!isPT && !isIRL && zoneMapES[low]) return zoneMapES[low];


    const regionMapES: Record<string, string> = {
      norte: t("North") || "North",
      centro: t("Center") || "Center",
      noreste: t("Northeast") || "Northeast",
      sur: t("South") || "South",
      madrid: "Madrid",
      barcelona: "Barcelona",
    };
    if (regionMapES[low]) return regionMapES[low];

    const regionMapPT: Record<string, string> = {
      lisboa: "Lisboa",
      porto: "Porto",
      alentejo: "Alentejo",
      alenteho: "Alenteho",
      algarve: "Algarve",
    };
    if (regionMapPT[low]) return regionMapPT[low];

   
    if (low === "mercado") return t("Market") as string;
    if (low === "area" || low === "área" || low === "region" || low === "región")
      return t("Area") as string;

    if (low === "ventas") return t("Sales") as string;
    if (low === "tickets") return t("Tickets") as string;
    if (low === "ticket medio" || low === "ticketmedio")
      return t("Average-ticket") as string;
    if (low === "totales") return t("Totals") as string;
    if (low === "total") return t("Total") as string;
    if (low === "comparables" || low === "comparable")
      return t("Comparables") as string;

    const direct = t(s) as string;
    return direct !== s ? direct : s;
  };

  if (Array.isArray(input)) return input.map((el) => translateExportKeys(el, t, isPT, isIRL));
  if (input && typeof input === "object") {
    const out: any = {};
    Object.keys(input).forEach((k) => {
      const nk = translateKey(k);
      if (nk !== "") out[nk] = translateExportKeys(input[k], t, isPT, isIRL);
    });
    return out;
  }
  return input;
}


function replaceLabelsInText(text: string, isPT: boolean, isIRL: boolean, t: (k: string) => string) {
  let out = text ?? "";

  const mapIRL: Array<[RegExp, string]> = [
    [/\bMadrid\b/gi,    "Dublin"],
    [/\bBarcelona\b/gi, t("Northern&Western") || "Northern & Western"],
    [/\bZona 3\b/gi,    t("Eastern&Midland")  || "Eastern & Midland"],
    [/\bZona 4\b/gi,    t("Southern")         || "Southern"],
    [/\bZona 5\b/gi,    ""],
    [/\bZona 6\b/gi,    ""],
  ];
  const mapPT: Array<[RegExp, string]> = [
    [/\bMadrid\b/gi,    "Lisboa"],
    [/\bBarcelona\b/gi, "Porto"],
    [/\bZona 3\b/gi,    t("North")  || "North"],
    [/\bZona 4\b/gi,    t("Center") || "Center"],
    [/\bZona 5\b/gi,    "Alentejo"],
    [/\bZona 6\b/gi,    "Algarve"],
  ];
  const mapES: Array<[RegExp, string]> = [
    [/\bZona 3\b/gi, t("North")     || "North"],
    [/\bZona 4\b/gi, t("Center")    || "Center"],
    [/\bZona 5\b/gi, t("Northeast") || "Northeast"],
    [/\bZona 6\b/gi, t("South")     || "South"],
  ];

  const activeMap = isIRL ? mapIRL : isPT ? mapPT : mapES;
  for (const [re, val] of activeMap) {
    out = out.replace(re, val);
  }
  return out;
}

const Graph = () => {
  const { t, i18n } = useTranslation();
  const { semanas, filters, user } = useContext(AppContext);

  const entorno = filters.entorno === Entorno.Comparable ? "Comparables" : "Totales";
  const isPT =
    (i18n.language || "").toLowerCase().startsWith("pt") ||
    (typeof window !== "undefined" &&
      /^pt[-.]/i.test(window.location.host.split(".")[0] || ""));
      const isIRL =
      typeof window !== "undefined" &&
      (window.location.hostname.includes("irl") ||
        window.location.hostname.includes("app"));
  

  const GET_GRAPH = gql`
    query (
      $yr1: Int!
      $wk1: Int!
      $yr2: Int!
      $wk2: Int!
      $var: Int
      $segment: String
      $segmentType: Int
    ) {
      graph(
        Graph: "Tendencias por region"
        WeekRange: {
          Start: { Year: $yr1, Week: $wk1 }
          End: { Year: $yr2, Week: $wk2 }
        }
        Filters: { Variables: $var, Segmento: $segment, SegmentType: $segmentType }
      )
    }
  `;

  const qry = useQuery(GET_GRAPH, {
    fetchPolicy: "no-cache",
    variables: {
      yr1: semanas[0].year,
      wk1: semanas[0].week,
      yr2: semanas[1].year,
      wk2: semanas[1].week,
      var: 0,
    },
  });

  let errorMsg = t("An-error-has-occur-when-recovering-data");
  let loading = qry.loading || qry.data === undefined;
  let error = !semanas || !!qry.error;
  let values: any = [];

  let data: any;
  if (!loading && !error && qry.data?.graph) {
    try {
      data = qry?.data?.graph;
      if (!data) throw new Error(errorMsg);
      if (data?.Error) throw new Error(`${data.Error}`);

      let segmentStr = filters.segmentos[filters.segment] || "Mercado";
      if (segmentStr === "Total") segmentStr = "Mercado";
      const segmentPos = data.filter((d: any) => Object.keys(d)[0] === segmentStr);
      const cData: any = Object.values(data[data.indexOf(segmentPos[0])])[0];

      const aux: any = Object.entries(cData[entorno])[filters.medidas][1];
      values = Object.values(aux);
    } catch (err: any) {
      error = true;
      errorMsg = err.message ?? errorMsg;
    }
  }

  const exportData = useMemo(
    () => (data ? translateExportKeys(data, t, isPT, isIRL) : data),
    [JSON.stringify(data), t, isPT, isIRL]
  );

  const [content, setContent] = useState("");
  let isLoading = loading || values.length === 0 || isNaN(values[0]);

  return (
    <div style={{ position: "relative" }}>
      <Title
        title={t("Regional-position")}
        error={!!error}
        files={{
          data: exportData,
          semanas,
          user,
          title: t("Regional-position"),
          leyenda: t("Area"),
        }}
      />
      {error && <MessageBar isMultiline={true}>{errorMsg}</MessageBar>}
      {!error && (
        <>
          <MapGraph
            className="map-container"
            setTooltipContent={(raw: string) => {
              setContent(replaceLabelsInText(raw, isPT, isIRL, t));
            }}
            values={values}
            loading={!!isLoading}
          />
        </>
      )}
      <div
        ref={(el) => {
          const w = (window as unknown) as any;
          const KEY = "__mvr_mousemove_handler__";
          if (el) {
            if (w[KEY]) return;
            const handler = (e: MouseEvent) => {
              el.style.setProperty("--mx", `${e.clientX}px`);
              el.style.setProperty("--my", `${e.clientY}px`);
            };
            w[KEY] = handler;
            window.addEventListener("mousemove", handler);
          } else {
            if (w[KEY]) {
              window.removeEventListener("mousemove", w[KEY]);
              delete w[KEY];
            }
          }
        }}
        style={{
          position: "fixed",
          inset: 0,
          pointerEvents: "none",
          zIndex: 1000,
        }}
      >
        {content !== "" && (
          <div
            style={{
              position: "fixed",
              transform:
                "translate(calc(var(--mx) + 12px), calc(var(--my) + 12px))",
              background: "white",
              padding: "0.5rem 0.25rem",
              color: "black",
              boxSizing: "border-box",
              pointerEvents: "none",
              zIndex: 1000,
              maxWidth: "260px",
              borderRadius: 4,
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              whiteSpace: "pre-wrap",
            }}
          >
            {content}
          </div>
        )}
      </div>
    </div>
  );
};

export default Graph;